import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Coins } from "lucide-react";

import { log } from "@/lib/log";
/**
 * Currency / country pill — lets the visitor manually override the
 * country detected by the server. Persists choice to `localStorage`
 * under `riskpedia:country` (read by the axios interceptor in `lib/api.js`).
 *
 * Triggers a soft reload (`location.reload`) on change so price chips
 * across the page refetch with the new locale.
 */
const OPTIONS = [
  { code: "IN", currency: "INR", label: "India · INR", flag: "🇮🇳" },
  { code: "US", currency: "USD", label: "United States · USD", flag: "🇺🇸" },
  { code: "GB", currency: "GBP", label: "United Kingdom · GBP", flag: "🇬🇧" },
  { code: "DE", currency: "EUR", label: "Eurozone · EUR", flag: "🇪🇺" },
];

const STORAGE_KEY = "riskpedia:country";

function readSaved() {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    // localStorage can throw in private-browsing / Safari ITP / sandboxed iframes.
    return null;
  }
}

function detectInitial() {
  const saved = readSaved();
  if (saved) return saved;
  try {
    const loc = (navigator.language || "").trim();
    const m = loc.match(/[-_]([A-Z]{2})\b/i);
    if (m) return m[1].toUpperCase();
  } catch (err) {
    // navigator.language is missing in some headless test runners — fall through.
    log.warn("Locale detection unavailable:", err?.message);
  }
  return "US";
}

export default function CurrencyPill() {
  const [code, setCode] = useState(detectInitial);

  useEffect(() => {
    // Keep state in sync across tabs.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) setCode(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const current = OPTIONS.find((o) => o.code === code) || OPTIONS[1];

  const pick = (next) => {
    if (next.code === code) return;
    try {
      localStorage.setItem(STORAGE_KEY, next.code);
    } catch (err) {
      // localStorage may be read-only in private mode; reload still picks up navigator.language.
      log.warn("CurrencyPill: localStorage write blocked", err);
    }
    setCode(next.code);
    // Soft reload so every cached price chip refetches with the new country.
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="hidden sm:flex items-center gap-1.5 rounded-full border border-rp-border bg-rp-surface px-3 py-1.5 text-xs font-medium text-rp-textDim hover:text-rp-text hover:border-rp-borderHover transition-colors"
          data-testid="currency-pill"
          aria-label="Change pricing currency"
          title={`Pricing in ${current.currency}`}
        >
          <span className="text-base leading-none" aria-hidden>{current.flag}</span>
          <span>{current.currency}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-rp-surface border-rp-border text-rp-text min-w-[200px]"
        data-testid="currency-menu"
      >
        <DropdownMenuLabel className="text-rp-textDim font-normal text-[10px] uppercase tracking-wider">
          <Coins className="mr-1 inline h-3 w-3" /> Pricing in
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-rp-border" />
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.code}
            onClick={() => pick(o)}
            className={`cursor-pointer focus:bg-rp-surface2 ${o.code === code ? "text-rp-accent" : ""}`}
            data-testid={`currency-opt-${o.code}`}
          >
            <span className="mr-2 text-base">{o.flag}</span>
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
